import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Brain, FileText, ExternalLink, AlertTriangle, Loader2, RefreshCcw, ShieldCheck, CheckSquare, Square, Sparkles, Upload, Wand2, Calculator, Handshake, MessageSquare, Pencil, Save, X, RotateCcw } from "lucide-react";
import PastPerformanceMatchCard from "@/components/PastPerformanceMatchCard";
import WinProbabilityExplainer from "@/components/WinProbabilityExplainer";
import OverageDialog from "@/components/OverageDialog";
import OpportunityNotes from "@/components/OpportunityNotes";
import { useAuth } from "@/context/AuthContext";

// Strip HTML tags (e.g. <p>, <br>, <div>) that SAM.gov sometimes injects into
// the raw description feed. We render as plain text so operators don't see
// naked markup or double-spaced paragraphs mid-sentence.
function stripHtml(raw) {
  if (!raw) return "";
  // Replace block-level closers with newlines so paragraphs still separate.
  let s = raw.replace(/<\s*(br|\/p|\/div|\/li|\/tr|\/h[1-6])\s*\/?\s*>/gi, "\n");
  // Drop everything else in angle brackets
  s = s.replace(/<[^>]+>/g, "");
  // Decode a handful of the most common HTML entities we see from SAM.gov
  s = s
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&rsquo;/gi, "'")
    .replace(/&lsquo;/gi, "'")
    .replace(/&ldquo;/gi, '"')
    .replace(/&rdquo;/gi, '"');
  // Collapse whitespace runs but preserve paragraph breaks
  s = s.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n");
  return s.trim();
}

const RECOMMENDATION_COLORS = {
  "Highly Recommended": "text-cyan-400 border-cyan-400 bg-cyan-400/5",
  "Recommended": "text-green-400 border-green-400 bg-green-400/5",
  "Consider Carefully": "text-amber-400 border-amber-400 bg-amber-400/5",
  "Not Recommended": "text-red-400 border-red-400 bg-red-400/5",
};

const STAGES = ["new", "ai_reviewing", "interested", "estimating", "in_progress", "needs_review", "submitted", "awarded", "lost", "follow_up"];

export default function OpportunityDetail() {
  const { noticeId } = useParams();
  const [opp, setOpp] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [checkingAmend, setCheckingAmend] = useState(false);
  const [complianceChecks, setComplianceChecks] = useState({});
  const [templates, setTemplates] = useState([]);
  const [chosenTemplate, setChosenTemplate] = useState("");
  const [existingProposal, setExistingProposal] = useState(null);
  const [pickingTemplate, setPickingTemplate] = useState(false);
  const [overageDetail, setOverageDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("overview"); // overview | notes
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const { user: authUser } = useAuth();
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/opportunities/${noticeId}`);
      setOpp(r.data);
      if (r.data.bid_analysis) setAnalysis(r.data.bid_analysis);
    } catch (err) {
      console.error("opportunity fetch failed:", err);
      toast.error("Not found");
    }
    try {
      const t = await api.get("/documents/templates");
      setTemplates(t.data);
    } catch (err) {
      // Non-fatal — the picker just stays empty.  Log for ops visibility.
      console.error("template list failed:", err);
    }
    try {
      const p = await api.get(`/proposals/${noticeId}`);
      setExistingProposal(p.data);
    } catch (err) {
      // Expected 404 when the opportunity has no proposal yet — swallow it,
      // but log 5xx so backend regressions are visible.
      const status = err?.response?.status;
      if (status && status >= 500) console.error("proposal fetch failed:", err);
    }
  }, [noticeId]);
  useEffect(() => { load(); }, [load]);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post(`/ai/bid-analysis/${noticeId}`);
      setAnalysis(r.data);
      toast.success("Bid analysis ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const draft = async (acceptOverage = false) => {
    setDrafting(true);
    try {
      const body = { template_doc_id: chosenTemplate || null };
      if (acceptOverage) body.accept_overage = true;
      const r = await api.post(`/ai/proposal/${noticeId}/generate`, body, { timeout: 300000 });
      setExistingProposal(r.data);
      setOverageDetail(null);
      // Refresh the sidebar meter so the operator sees the new count immediately.
      window.dispatchEvent(new CustomEvent("bidforge:usage-refresh"));
      toast.success("Proposal draft ready");
      nav(`/proposals/${noticeId}`);
    } catch (e) {
      // 402 monthly_proposal_cap_reached — pop the overage confirmation dialog.
      if (e?.response?.status === 402 && e?.response?.data?.detail?.error === "monthly_proposal_cap_reached") {
        setOverageDetail(e.response.data.detail);
        setDrafting(false);
        return;
      }
      // If gateway timed out, try polling for the result briefly
      if (e?.code === "ECONNABORTED" || e?.response?.status === 502 || e?.response?.status === 504) {
        toast.message("Still generating… we'll check for it");
        for (let i = 0; i < 12; i++) {
          await new Promise(res => setTimeout(res, 5000));
          try {
            const p = await api.get(`/proposals/${noticeId}`);
            if (p.data?.sections && Object.keys(p.data.sections).length >= 10) {
              setExistingProposal(p.data);
              toast.success("Proposal draft ready");
              nav(`/proposals/${noticeId}`);
              return;
            }
          } catch (pollErr) {
            // Transient 5xx during polling is expected while the backend is
            // still generating — log so we can spot true regressions.
            const s = pollErr?.response?.status;
            if (s && s !== 404 && s !== 502 && s !== 504) {
              console.error("proposal poll failed:", pollErr);
            }
          }
        }
        toast.error("Generation is taking longer than expected — check Proposals shortly.");
      } else {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : (d?.message || "Draft failed"));
      }
    } finally { setDrafting(false); }
  };

  const updateStage = async (stage) => {
    try {
      await api.put(`/opportunities/${noticeId}/stage`, { stage });
      setOpp({ ...opp, pipeline_stage: stage });
      toast.success(`Moved to ${stage.replace("_", " ")}`);
    } catch (err) {
      console.error("stage update failed:", err);
      toast.error("Failed");
    }
  };

  const saveDescription = async () => {
    if (savingDesc) return;
    setSavingDesc(true);
    try {
      const r = await api.patch(`/opportunities/${noticeId}/description`, { description: descDraft });
      setOpp((prev) => ({
        ...prev,
        description: r.data.description,
        description_original: prev?.description_original ?? prev?.description ?? "",
      }));
      setEditingDesc(false);
      toast.success("Description updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save description");
    } finally {
      setSavingDesc(false);
    }
  };

  const restoreOriginalDescription = async () => {
    if (!opp?.description_original) return;
    if (!confirm("Restore the original SAM.gov description? Your edits will be replaced.")) return;
    setSavingDesc(true);
    try {
      const r = await api.patch(`/opportunities/${noticeId}/description`, { description: opp.description_original });
      setOpp((prev) => ({ ...prev, description: r.data.description }));
      setEditingDesc(false);
      toast.success("Restored original description");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setSavingDesc(false);
    }
  };

  const checkAmendments = async () => {
    setCheckingAmend(true);
    try {
      const r = await api.post(`/opportunities/${noticeId}/check-amendments`);
      if (r.data.changed) {
        toast.success(`${r.data.changes.length} amendment(s) detected`);
        await load();
      } else {
        toast.success("No new amendments");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setCheckingAmend(false); }
  };

  const toggleCheck = (item) => setComplianceChecks(c => ({ ...c, [item]: !c[item] }));

  const [ingesting, setIngesting] = useState(false);
  const ingestAttachments = async () => {
    if (ingesting) return;
    setIngesting(true);
    try {
      const r = await api.post(`/opportunities/${noticeId}/ingest-attachments`, {}, { timeout: 240000 });
      toast.success(`Read ${r.data.files_processed} attachment(s) — ${r.data.attachment_takeoff?.summary || "takeoff complete"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attachment ingestion failed");
    } finally { setIngesting(false); }
  };

  const autoPickTemplate = async () => {
    setPickingTemplate(true);
    try {
      const r = await api.post(`/ai/pick-template/${noticeId}`);
      if (r.data.best_doc_id) {
        setChosenTemplate(r.data.best_doc_id);
        toast.success(`Selected: ${r.data.best_doc_name} (${r.data.confidence}% match) — ${r.data.reason}`);
      } else {
        toast.message(r.data.reason || "No good template match found");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Pick failed"); }
    finally { setPickingTemplate(false); }
  };

  if (!opp) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <div className="p-8 stagger-in max-w-6xl" data-testid="opportunity-detail">
      <Link to="/opportunities" className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2 mb-6" data-testid="back-btn"><ArrowLeft className="w-3.5 h-3.5" />BACK TO OPPORTUNITIES</Link>

      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-3 flex items-center gap-3 flex-wrap">
          <span>{opp.agency}</span>
          {opp.sub_agency && <><span className="text-slate-600">·</span><span>{opp.sub_agency}</span></>}
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-4">{opp.title}</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <span className={`font-display font-black text-3xl ${opp.match_score >= 85 ? "text-cyan-400" : "text-amber-400"}`}>{opp.match_score}<span className="mono-label text-slate-500 ml-2">MATCH</span></span>
          {opp.url && <a href={opp.url} target="_blank" rel="noreferrer" className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2" data-testid="sam-link">SAM.gov <ExternalLink className="w-3.5 h-3.5" /></a>}
          <button onClick={checkAmendments} disabled={checkingAmend} className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2 disabled:opacity-50" data-testid="check-amendments">
            <RefreshCcw className={`w-3.5 h-3.5 ${checkingAmend ? "animate-spin" : ""}`} />{checkingAmend ? "Checking…" : "Check amendments"}
          </button>
        </div>

        {opp.amendment_history?.length > 0 && (
          <div className="mt-4 bg-amber-400/5 border border-amber-400/30 p-4" data-testid="amendment-history">
            <div className="mono-label text-amber-400 mb-2 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" />AMENDMENT HISTORY · {opp.amendment_history.length}</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {opp.amendment_history.slice(-3).reverse().map((a, i) => (
                <div key={i} className="text-xs text-slate-300">
                  <div className="mono-label text-slate-500">{new Date(a.at).toLocaleString()}</div>
                  {a.changes.map((c, j) => <div key={j}><span className="text-amber-400">{c.field}</span>: {c.old} → {c.new}</div>)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-[#0D1424] border border-white/10 p-5"><div className="mono-label text-slate-500 mb-2">NAICS</div><div className="font-mono text-cyan-400">{opp.naics || "—"}</div></div>
        <div className="bg-[#0D1424] border border-white/10 p-5"><div className="mono-label text-slate-500 mb-2">DEADLINE</div><div className="font-mono">{opp.response_deadline ? new Date(opp.response_deadline).toLocaleString() : "—"}</div></div>
        <div className="bg-[#0D1424] border border-white/10 p-5"><div className="mono-label text-slate-500 mb-2">SET ASIDE</div><div className="text-sm">{opp.set_aside || "Full & Open"}</div></div>
      </div>

      {/* Tab strip — Overview (default) vs Notes (team thread) */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-6" data-testid="opp-tabs">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2.5 mono-label border-b-2 -mb-px transition-colors ${activeTab === "overview" ? "border-cyan-400 text-cyan-400" : "border-transparent text-slate-500 hover:text-slate-300"}`}
          data-testid="tab-overview"
        >
          <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" />OVERVIEW</span>
        </button>
        <button
          onClick={() => setActiveTab("notes")}
          className={`px-4 py-2.5 mono-label border-b-2 -mb-px transition-colors ${activeTab === "notes" ? "border-cyan-400 text-cyan-400" : "border-transparent text-slate-500 hover:text-slate-300"}`}
          data-testid="tab-notes"
        >
          <span className="flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" />NOTES</span>
        </button>
      </div>

      {activeTab === "notes" ? (
        <div className="mb-8">
          <OpportunityNotes noticeId={noticeId} currentUserId={authUser?.user_id} />
        </div>
      ) : (
      <>

      <div className="bg-[#0D1424] border border-white/10 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="mono-label text-cyan-400 mb-2">// SOLICITATION</div>
            <h3 className="font-display font-bold text-xl">Description</h3>
            {opp.description_edited_by && !editingDesc && (
              <div className="mono-label text-slate-500 text-[10px] mt-1">EDITED BY TEAM · {opp.description_edited_at ? new Date(opp.description_edited_at).toLocaleString() : ""}</div>
            )}
          </div>
          {!editingDesc ? (
            <button
              onClick={() => { setDescDraft(opp.description || ""); setEditingDesc(true); }}
              className="mono-label text-slate-500 hover:text-cyan-400 inline-flex items-center gap-1 shrink-0"
              data-testid="edit-description-btn"
            >
              <Pencil className="w-3.5 h-3.5" />EDIT
            </button>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              {opp.description_original && (
                <button
                  onClick={restoreOriginalDescription}
                  disabled={savingDesc}
                  className="mono-label text-slate-500 hover:text-amber-400 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="restore-description-btn"
                  title="Restore original SAM.gov description"
                >
                  <RotateCcw className="w-3.5 h-3.5" />RESTORE
                </button>
              )}
              <button
                onClick={() => setEditingDesc(false)}
                disabled={savingDesc}
                className="mono-label text-slate-500 hover:text-slate-300 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="cancel-description-btn"
              >
                <X className="w-3.5 h-3.5" />CANCEL
              </button>
              <button
                onClick={saveDescription}
                disabled={savingDesc || !descDraft.trim()}
                className="bf-pill px-3 py-1.5 mono-label inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="save-description-btn"
              >
                {savingDesc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                SAVE
              </button>
            </div>
          )}
        </div>
        {editingDesc ? (
          <textarea
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            className="w-full bg-black/40 border border-white/10 focus:border-cyan-400/60 outline-none text-sm text-slate-200 p-3 min-h-[320px] font-mono leading-relaxed"
            data-testid="description-textarea"
            autoFocus
          />
        ) : (
          <div className="text-slate-300 leading-relaxed text-sm whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid="solicitation-description">
            {(() => {
              const raw = opp.description || "";
              if (!raw || raw.startsWith("http")) {
                return <span className="text-slate-500 italic">Description not available in feed. {opp.url && <a href={opp.url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">View on SAM.gov ↗</a>}</span>;
              }
              return stripHtml(raw);
            })()}
          </div>
        )}
      </div>

      {/* Full RFP Takeoff — reads every SAM.gov attachment and extracts the
          facts the synopsis doesn't carry (magnitude of construction, true
          set-aside, wage determination, bond %, insurance minimums, site
          visit, submittal reqs, scope quantities). Attachment wins semantics:
          set-aside + bonding + wage det + insurance on the opp are replaced
          with what the RFP body actually says. */}
      {opp.resource_links?.length > 0 && !opp.attachment_takeoff && (
        <div className="bg-cyan-400/5 border border-cyan-400/40 p-5 mb-6" data-testid="ingest-cta">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="mono-label text-cyan-400 mb-1">// FULL TAKEOFF</div>
              <div className="font-display font-bold text-white mb-1">Read every SAM.gov attachment before bidding</div>
              <div className="text-sm text-slate-300 mb-3">
                This opportunity has <span className="font-mono text-cyan-400">{opp.resource_links.length}</span> attachment{opp.resource_links.length === 1 ? "" : "s"} (SF-1442, drawings, specs, wage determination). Ingest them to catch magnitude, real set-aside, bond % and scope quantities the synopsis doesn't carry.
              </div>
              <button
                onClick={ingestAttachments}
                disabled={ingesting}
                className="bf-pill px-5 py-2 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="ingest-attachments-btn"
              >
                {ingesting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />READING ATTACHMENTS…</> : <><FileText className="w-3.5 h-3.5" />INGEST ATTACHMENTS</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {opp.attachment_takeoff && (
        <div className="bg-[#0D1424] border border-emerald-400/40 p-5 mb-6" data-testid="takeoff-summary">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="mono-label text-emerald-400 mb-1">// TAKEOFF · {opp.attachment_takeoff.files?.length || 0} FILE(S)</div>
              <div className="font-display font-bold text-white">{opp.attachment_takeoff.summary || "Attachment takeoff complete"}</div>
              {opp.attachment_takeoff.generated_at && (
                <div className="mono-label text-slate-500 mt-1">Ingested {new Date(opp.attachment_takeoff.generated_at).toLocaleString()}</div>
              )}
            </div>
            <button
              onClick={ingestAttachments}
              disabled={ingesting}
              className="text-[10px] font-mono font-bold tracking-widest text-slate-400 hover:text-cyan-400 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="reingest-btn"
            >
              {ingesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}RE-INGEST
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {opp.attachment_takeoff.magnitude?.low && (
              <div className="bg-black/20 border border-white/5 p-3" data-testid="takeoff-magnitude">
                <div className="mono-label text-slate-500 mb-1">MAGNITUDE</div>
                <div className="font-mono text-emerald-400 text-lg">
                  ${Number(opp.attachment_takeoff.magnitude.low).toLocaleString()} – ${Number(opp.attachment_takeoff.magnitude.high || opp.attachment_takeoff.magnitude.low).toLocaleString()}
                </div>
                {opp.attachment_takeoff.magnitude.verbatim && (
                  <div className="text-[11px] text-slate-500 mt-1 italic">"{opp.attachment_takeoff.magnitude.verbatim.slice(0, 140)}"</div>
                )}
              </div>
            )}
            {opp.attachment_takeoff.set_aside?.type && (
              <div className="bg-black/20 border border-white/5 p-3" data-testid="takeoff-set-aside">
                <div className="mono-label text-slate-500 mb-1">SET-ASIDE (from RFP body)</div>
                <div className="font-mono text-emerald-400">{opp.attachment_takeoff.set_aside.type}</div>
                {opp.attachment_takeoff._synopsis_before?.set_aside && opp.attachment_takeoff._synopsis_before.set_aside !== opp.attachment_takeoff.set_aside.type && (
                  <div className="text-[11px] text-amber-400 mt-1">Synopsis said: <span className="line-through">{opp.attachment_takeoff._synopsis_before.set_aside}</span></div>
                )}
              </div>
            )}
            {opp.attachment_takeoff.wage_determination?.act && opp.attachment_takeoff.wage_determination.act !== "None" && (
              <div className="bg-black/20 border border-white/5 p-3" data-testid="takeoff-wage">
                <div className="mono-label text-slate-500 mb-1">WAGE DETERMINATION</div>
                <div className="font-mono text-emerald-400">{opp.attachment_takeoff.wage_determination.act}{opp.attachment_takeoff.wage_determination.wd_number ? ` · ${opp.attachment_takeoff.wage_determination.wd_number}` : ""}</div>
              </div>
            )}
            {opp.attachment_takeoff.bonding && (opp.attachment_takeoff.bonding.performance_bond_pct || opp.attachment_takeoff.bonding.bid_bond_pct) && (
              <div className="bg-black/20 border border-white/5 p-3" data-testid="takeoff-bonding">
                <div className="mono-label text-slate-500 mb-1">BONDING</div>
                <div className="font-mono text-emerald-400 text-xs space-y-0.5">
                  {opp.attachment_takeoff.bonding.bid_bond_pct && <div>Bid bond: {opp.attachment_takeoff.bonding.bid_bond_pct}%</div>}
                  {opp.attachment_takeoff.bonding.performance_bond_pct && <div>Performance: {opp.attachment_takeoff.bonding.performance_bond_pct}%</div>}
                  {opp.attachment_takeoff.bonding.payment_bond_pct && <div>Payment: {opp.attachment_takeoff.bonding.payment_bond_pct}%</div>}
                </div>
              </div>
            )}
            {opp.attachment_takeoff.site_visit?.mandatory && (
              <div className="bg-black/20 border border-white/5 p-3" data-testid="takeoff-site-visit">
                <div className="mono-label text-slate-500 mb-1">SITE VISIT · MANDATORY</div>
                <div className="font-mono text-amber-400 text-xs">{opp.attachment_takeoff.site_visit.date || "See RFP"}{opp.attachment_takeoff.site_visit.location ? ` · ${opp.attachment_takeoff.site_visit.location.slice(0, 60)}` : ""}</div>
              </div>
            )}
            {opp.attachment_takeoff.scope_quantities?.length > 0 && (
              <div className="bg-black/20 border border-white/5 p-3 md:col-span-2" data-testid="takeoff-scope">
                <div className="mono-label text-slate-500 mb-2">SCOPE QUANTITIES · {opp.attachment_takeoff.scope_quantities.length}</div>
                <div className="grid md:grid-cols-2 gap-1 text-xs font-mono">
                  {opp.attachment_takeoff.scope_quantities.slice(0, 10).map((sq, i) => (
                    <div key={i} className="text-slate-300"><span className="text-emerald-400">{sq.quantity} {sq.unit}</span> · {sq.item}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline stage */}
      <div className="bg-[#0D1424] border border-white/10 p-6 mb-6">
        <div className="mono-label text-cyan-400 mb-3">// PIPELINE STAGE</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map(s => (
            <button key={s} onClick={() => updateStage(s)} className={`mono-label px-3 py-1.5 border transition-colors ${opp.pipeline_stage === s ? "border-cyan-400 text-cyan-400 bg-cyan-400/10" : "border-white/10 text-slate-400 hover:border-white/30"}`} data-testid={`stage-${s}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Past performance match preview — read-only, no credit spend. Lets the operator
          see which references the AI will cite (with match scores) and add missing
          past performance before generating the proposal. */}
      <div className="mb-6">
        <PastPerformanceMatchCard noticeId={noticeId} />
      </div>

      {/* Teaming deep-links — recently awarded primes AND certified subs for this
          NAICS both live on the Connect page. One click over there with everything
          pre-filled and the search auto-fired. */}
      {opp.naics && (
        <div className="mb-6 grid md:grid-cols-2 gap-3">
          <Link
            to={`/connect?tab=search&naics=${encodeURIComponent(opp.naics)}&auto=1`}
            className="flex items-center justify-between gap-4 border border-violet-400/40 bg-violet-400/5 hover:bg-violet-400/10 p-4 transition-colors"
            data-testid="see-primes-link"
          >
            <div className="flex items-center gap-3">
              <Handshake className="w-5 h-5 text-violet-300" />
              <div>
                <div className="mono-label text-violet-300 mb-1">// TEAMING · PRIMES</div>
                <div className="text-sm text-white">Primes who recently won NAICS <span className="font-mono text-violet-300">{opp.naics}</span></div>
              </div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-violet-300" />
          </Link>
          <Link
            to={`/connect?tab=subs&naics=${encodeURIComponent(opp.naics)}&auto=1`}
            className="flex items-center justify-between gap-4 border border-emerald-400/40 bg-emerald-400/5 hover:bg-emerald-400/10 p-4 transition-colors"
            data-testid="find-subs-link"
          >
            <div className="flex items-center gap-3">
              <Handshake className="w-5 h-5 text-emerald-300" />
              <div>
                <div className="mono-label text-emerald-300 mb-1">// TEAMING · SUBS</div>
                <div className="text-sm text-white">Certified subs in NAICS <span className="font-mono text-emerald-300">{opp.naics}</span></div>
              </div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-emerald-300" />
          </Link>
        </div>
      )}

      {/* AI Bid/No-Bid */}
      <div className={`bg-[#0D1424] border p-6 mb-6 relative ${analyzing ? "tracer-border" : "border-white/10"}`}>
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><Brain className="w-3.5 h-3.5" />BID / NO-BID ENGINE</div>
            <h3 className="font-display font-bold text-2xl">Executive Briefing</h3>
          </div>
          <div className="flex gap-2 items-center">
            <WinProbabilityExplainer noticeId={noticeId} aiProbability={analysis?.win_probability} />
            <button onClick={analyze} disabled={analyzing} className="bg-cyan-400 text-[#070B14] px-5 py-2.5 mono-label hover:bg-cyan-300 transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="analyze-btn">
              {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing</> : <>{analysis ? "Re-analyze" : "Run analysis"}</>}
            </button>
          </div>
        </div>

        {!analysis && !analyzing && <div className="text-slate-500 py-8 text-center">Click "Run analysis" for an AI-generated bid recommendation.</div>}

        {analysis && (
          <>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className={`border p-5 ${RECOMMENDATION_COLORS[analysis.recommendation] || "border-white/10"}`}>
                <div className="mono-label mb-2 opacity-70">RECOMMENDATION</div>
                <div className="font-display font-black text-2xl">{analysis.recommendation}</div>
              </div>
              <div className="border border-white/10 p-5">
                <div className="mono-label mb-2 text-slate-500 flex items-center justify-between">
                  <span>WIN PROBABILITY</span>
                  <WinProbabilityExplainer noticeId={noticeId} aiProbability={analysis.win_probability} />
                </div>
                <div className="font-display font-black text-3xl text-cyan-400">{analysis.win_probability}<span className="text-lg text-slate-500">%</span></div>
                <div className="h-1 bg-white/5 mt-2"><div className="h-full bg-cyan-400" style={{ width: `${analysis.win_probability}%` }} /></div>
              </div>
            </div>

            <Section title="Executive Summary" content={analysis.executive_summary} />
            <Section title="Scope of Work" content={analysis.scope_of_work} />
            <Section title="Rationale" content={analysis.rationale} />

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <ListBlock title="Required Materials" items={analysis.required_materials} />
              <ListBlock title="Required Equipment" items={analysis.required_equipment} />
              <ListBlock title="Subcontractors Needed" items={analysis.subcontractors_needed} />
              <ListBlock title="Compliance Requirements" items={analysis.compliance_requirements} />
              <ListBlock title="Key Risks" items={analysis.key_risks} icon={AlertTriangle} accent="text-amber-400" />
              <ListBlock title="Red Flags" items={analysis.red_flags} icon={AlertTriangle} accent="text-red-400" />
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <Stat label="Duration" value={analysis.estimated_duration} />
              <Stat label="Bonding" value={analysis.bonding_requirements} />
              <Stat label="Insurance" value={analysis.insurance_requirements} />
              <Stat label="Wage Determinations" value={analysis.wage_determinations} />
              <Stat label="Licensing" value={analysis.licensing_requirements} />
              <Stat label="Competition" value={analysis.competitive_difficulty} />
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-6">
              <Section title="Profitability" content={analysis.profitability} />
              <Section title="Past Performance Fit" content={analysis.past_performance_fit} />
              <Section title="Geographic Fit" content={analysis.geographic_fit} />
            </div>

            {analysis.compliance_requirements?.length > 0 && (
              <div className="mt-8 border border-cyan-400/30 bg-cyan-400/5 p-5" data-testid="compliance-matrix">
                <div className="mono-label text-cyan-400 mb-4 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" />COMPLIANCE MATRIX</div>
                <div className="mb-3 text-xs text-slate-400">Track each requirement. Progress: {Object.values(complianceChecks).filter(Boolean).length} / {analysis.compliance_requirements.length}</div>
                <div className="h-1 bg-white/5 mb-4"><div className="h-full bg-cyan-400 transition-all" style={{ width: `${(Object.values(complianceChecks).filter(Boolean).length / analysis.compliance_requirements.length) * 100}%` }} /></div>
                <div className="grid md:grid-cols-2 gap-2">
                  {analysis.compliance_requirements.map((req, i) => {
                    const key = `req-${i}`;
                    const checked = !!complianceChecks[key];
                    return (
                      <button key={i} onClick={() => toggleCheck(key)} className={`text-left p-3 border transition-colors flex items-start gap-3 ${checked ? "border-cyan-400/50 bg-cyan-400/10 text-white" : "border-white/10 hover:border-white/30 text-slate-300"}`} data-testid={`compliance-${i}`}>
                        {checked ? <CheckSquare className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> : <Square className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />}
                        <span className={`text-sm ${checked ? "line-through opacity-70" : ""}`}>{req}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* AI PROPOSAL GENERATOR — always visible under Bid/No-Bid */}
      <div className={`bg-[#0D1424] border p-6 mb-6 relative ${drafting ? "tracer-border" : "border-cyan-400/40"}`} data-testid="proposal-generator">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="mono-label text-cyan-400 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />AI PROPOSAL GENERATOR</div>
            <h3 className="font-display font-bold text-2xl">14-section proposal, written in your voice</h3>
            <p className="text-slate-400 text-sm mt-2 max-w-2xl">Uses your company profile, uploaded documents, and — optionally — a previously won proposal as a style template. Every section is tailored to this solicitation.</p>
          </div>
          {existingProposal && (
            <Link to={`/proposals/${noticeId}`} className="mono-label text-slate-400 hover:text-cyan-400 flex items-center gap-2" data-testid="open-existing-proposal">
              <FileText className="w-3.5 h-3.5" />OPEN DRAFT ({Object.keys(existingProposal.sections || {}).length}/14)
            </Link>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="md:col-span-2">
            <div className="mono-label text-slate-400 mb-2 flex items-center justify-between flex-wrap gap-2">
              <span>MODEL STYLE AFTER (optional)</span>
              <div className="flex items-center gap-3">
                {templates.length > 1 && (
                  <button onClick={autoPickTemplate} disabled={pickingTemplate} className="text-cyan-400 hover:text-cyan-300 normal-case flex items-center gap-1 disabled:opacity-50" data-testid="auto-pick-template">
                    {pickingTemplate ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}Let AI pick
                  </button>
                )}
                <Link to="/documents" className="text-cyan-400 hover:text-cyan-300 normal-case flex items-center gap-1" data-testid="upload-template-link">
                  <Upload className="w-3 h-3" />Upload
                </Link>
              </div>
            </div>
            <select value={chosenTemplate} onChange={(e) => setChosenTemplate(e.target.value)} className="w-full bg-[#070B14] border border-white/10 focus:border-cyan-400 px-3 py-2.5 text-sm outline-none" data-testid="template-select">
              <option value="">— No template · use company profile only —</option>
              {templates.map(t => (
                <option key={t.doc_id} value={t.doc_id}>
                  {t.doc_type === "won_proposal" ? "🏆 " : ""}{t.name} · {(t.doc_type || "").replace(/_/g, " ")}
                </option>
              ))}
            </select>
            {templates.length === 0 && (
              <div className="text-xs text-slate-500 mt-2">
                No proposals uploaded yet. Head to <Link to="/documents" className="text-cyan-400 hover:text-cyan-300">Documents</Link> and upload a previous winning proposal (mark it as <span className="mono-label text-cyan-400">won proposal</span>) so the AI can model this new draft after it.
              </div>
            )}
          </div>
          <div className="flex items-end">
            <button onClick={() => draft(false)} disabled={drafting} className="w-full bg-cyan-400 text-[#070B14] px-5 py-3 font-display font-bold text-sm tracking-wider uppercase hover:bg-cyan-300 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="draft-btn">
              {drafting ? <><Loader2 className="w-4 h-4 animate-spin" />DRAFTING…</> : <><Sparkles className="w-4 h-4" />{existingProposal ? "REGENERATE" : "GENERATE PROPOSAL"}</>}
            </button>
          </div>
        </div>

        {chosenTemplate && (
          <div className="mono-label text-cyan-400 border border-cyan-400/30 bg-cyan-400/5 px-3 py-2" data-testid="template-active">
            ◉ AI will match the tone, structure & phrasing of <span className="text-white">{templates.find(t => t.doc_id === chosenTemplate)?.name}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-2 border-t border-white/5">
          <Link to={`/opportunities/${noticeId}/estimate`} className="mono-label border border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 px-4 py-2 flex items-center gap-2" data-testid="open-estimate">
            <Calculator className="w-3.5 h-3.5" />OPEN ESTIMATING AGENT
          </Link>
          <button
            onClick={async () => {
              try {
                const r = await api.post("/takeoffs", {
                  project_name: (analysis?.title || opp?.title || "Federal opportunity").slice(0, 120),
                  address: opp?.place_of_performance || null,
                  client_name: opp?.agency || null,
                  scope_summary: analysis?.scope_of_work || opp?.description?.slice(0, 500) || null,
                  source_type: "federal",
                  notice_id: noticeId,
                  trades: ["all"],
                });
                toast.success("Takeoff created — opening editor…");
                nav(`/takeoffs/${r.data.takeoff_id}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not create takeoff");
              }
            }}
            className="mono-label border border-amber-400/50 text-amber-400 hover:bg-amber-400/10 px-4 py-2 flex items-center gap-2"
            data-testid="open-takeoff"
          >
            <Calculator className="w-3.5 h-3.5" />BUILD QUANTITY TAKEOFF
          </button>
          {existingProposal && (
            <Link to={`/proposals/${noticeId}`} className="mono-label border border-white/20 hover:border-cyan-400 hover:text-cyan-400 px-4 py-2 flex items-center gap-2" data-testid="open-proposal">
              <FileText className="w-3.5 h-3.5" />OPEN PROPOSAL WORKSPACE
            </Link>
          )}
        </div>

        {drafting && (
          <div className="mt-4 text-xs text-slate-400">
            <div className="mono-label text-cyan-400 mb-2 animate-pulse">◉ CLAUDE SONNET 4.5 · WRITING 14 SECTIONS</div>
            This normally takes 60-90 seconds. You can navigate away — the draft will land in your Proposals when ready.
          </div>
        )}
      </div>
      </>
      )}
      <OverageDialog
        open={!!overageDetail}
        detail={overageDetail}
        onCancel={() => setOverageDetail(null)}
        onConfirm={() => draft(true)}
        busy={drafting}
      />
    </div>
  );
}

function Section({ title, content }) {
  return (
    <div className="mt-5">
      <div className="mono-label text-slate-500 mb-2">{title}</div>
      <div className="text-sm text-slate-300 leading-relaxed">{content}</div>
    </div>
  );
}

function ListBlock({ title, items, icon: Icon, accent = "text-cyan-400" }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border border-white/10 p-4">
      <div className={`mono-label mb-3 flex items-center gap-2 ${accent}`}>{Icon && <Icon className="w-3.5 h-3.5" />}{title}</div>
      <ul className="space-y-1.5 text-sm text-slate-300">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-slate-600">→</span><span>{it}</span></li>)}
      </ul>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 p-4">
      <div className="mono-label text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-white">{value || "—"}</div>
    </div>
  );
}
